import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import './Recorder.css';

/**
 * The scripted "finger" the demo tabs use to press their own controls.
 *
 * Extracted from RecorderTouchDot (tab 3) when tab 4 needed the same thing:
 * one implementation, two scripts. The positioning rules it encodes were all
 * learned on tab 3 and are the reason it is worth sharing rather than copying.
 *
 * A dot rather than a mouse arrow: the demo plays on phones and tablets too,
 * where a cursor would be a fiction, and one element beats two kept in sync by
 * a media query.
 */
export interface TouchDotProps {
  /**
   * The current target's NAME, not the element. Changing it is what triggers a
   * re-measure, so a script can move the dot without the caller holding an
   * element, and — critically — a re-render that does NOT change the aim leaves
   * the dot exactly where it is.
   *
   * That second half is the whole reason this is a name and not an element:
   * pressing a control usually swaps the thing underneath it (tab 3's record row
   * becomes the transport row; tab 4's owner cell becomes a text field), and a
   * dot that re-measured on every render would jump away at the exact moment the
   * press is supposed to read as having caused the change.
   */
  aim: string | null;
  /**
   * Resolves the element for the current aim. Called only when `aim` changes.
   * Kept in a ref internally, so callers may pass a fresh closure each render
   * without re-triggering the measurement.
   */
  resolve: () => HTMLElement | null;
  /**
   * Optional scroll container to bring the target into view before aiming.
   *
   * Needed because a control being IN the DOM is not the same as being on
   * screen: tab 4's scroll region is 552px tall at 376 and the row its script
   * edits sits 99px below that, so the whole beat played in clipped overflow —
   * measurably "landing on its target" and invisible. Scrolling to it is also
   * what a real user does, so it costs nothing in fidelity.
   *
   * Omit it (as tab 3 does) and nothing scrolls.
   */
  scroller?: () => HTMLElement | null;
  visible: boolean;
  /** True during the press beat — the dot contracts and throws a ring. */
  pressing: boolean;
}

/** Kept clear of the scroller's edges, so a target never lands flush against one. */
const SCROLL_PAD = 20;

/**
 * How long the dot takes to travel, as a function of how far it is going.
 *
 * This was a flat 780ms in the CSS, tuned for the long journeys tabs 3 and 4
 * make across the whole shell. Tab 5's composer asks it to step down a list of
 * catalog rows about 26px apart, and 780ms to cross 26px does not read as a
 * hand moving — it reads as the dot being stuck, and it makes every press land
 * while the dot is still in transit, which is the exact correction tab 4 needed.
 *
 * A floor of 180ms rather than 0 so a short hop is still a visible MOVE and not
 * a teleport; the old 780 stays the ceiling so nothing that looked right before
 * changes. Linear in between, which is not how a real hand accelerates, but the
 * easing curve already carries that — this only sets the budget.
 */
const travelMs = (dx: number, dy: number) =>
  Math.round(Math.min(780, Math.max(180, 120 + Math.hypot(dx, dy) * 1.15)));

export default function TouchDot({ aim, resolve, scroller, visible, pressing }: TouchDotProps) {
  const layerRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [travel, setTravel] = useState(780);
  /* The same value as `pos`, in a ref, because the measuring effect keys on
     `aim` alone and would read a stale `pos` from its closure — and the whole
     point of the distance is the leg it is ABOUT to make. */
  const posRef = useRef<{ x: number; y: number } | null>(null);
  /* Suppresses the travel transition for the one frame the dot is placed at its
     entry offset — otherwise it would visibly slide in from wherever the
     previous leg left it, or from 0,0 on the very first aim. */
  const [instant, setInstant] = useState(true);
  const placed = useRef(false);

  /* Out of the dep list on purpose (see `resolve` above): callers build this
     from refs and state, so it changes identity constantly, and depending on it
     would re-measure on every render — the failure mode `aim` exists to avoid. */
  const resolveRef = useRef(resolve);
  resolveRef.current = resolve;
  const scrollerRef = useRef(scroller);
  scrollerRef.current = scroller;

  useLayoutEffect(() => {
    if (!aim) return;
    const layer = layerRef.current;
    if (!layer) return;
    const target = resolveRef.current();
    if (!target) return;

    /* Bring the target into view, then aim at where it is ABOUT to be.
       The scroll is smooth, so the rect we can measure now is the pre-scroll
       one — hence `shift` is subtracted rather than the rect re-read. Reading
       it after an animated scroll would give the old position anyway, and
       waiting for the animation would put the dot's arrival a beat late. */
    let shift = 0;
    const sc = scrollerRef.current?.();
    /* 🚨 ONLY IF THE TARGET IS ACTUALLY INSIDE IT. A caller supplies one
       scroller for a whole script, but a script aims at things outside it —
       tab 5 picks catalog rows from a scrolling rail and then presses Save &
       Publish, which is in the dialog footer. Without this test the effect
       computed a shift for a container the button does not live in, scrolled
       that container to its end, and subtracted the distance from the dot's y:
       the press landed EXACTLY the scroller's remaining scroll above the
       button, every time. Measured at the final press: -115px at a 335-572
       shell in English (115px of catalog left to scroll) and -17px in Arabic at
       768, where the rail had only 17px left. It read as the cursor pressing
       the middle of the section list (owner, 2026-09-07: "the last click in the
       mobile view doesn't click on save and publish -> clicks somewhere in the
       middle").
       It looked width-specific and was not: the rule is simply that the rail
       has to still be scrollable, which above 620 it usually is not. That is
       why it hid at desktop and why AR at 768 failed while EN at 768 passed. */
    if (sc && sc.contains(target)) {
      const sb = sc.getBoundingClientRect();
      const tb = target.getBoundingClientRect();
      if (tb.bottom > sb.bottom - SCROLL_PAD) shift = tb.bottom - (sb.bottom - SCROLL_PAD);
      else if (tb.top < sb.top + SCROLL_PAD) shift = tb.top - (sb.top + SCROLL_PAD);
      // Clamp to what the container can actually scroll, or the dot would aim
      // at a position the list never reaches.
      shift = Math.max(-sc.scrollTop, Math.min(shift, sc.scrollHeight - sc.clientHeight - sc.scrollTop));
      if (shift !== 0) sc.scrollTo({ top: sc.scrollTop + shift, behavior: 'smooth' });
    }

    const r = layer.getBoundingClientRect();
    const b = target.getBoundingClientRect();
    const to = {
      x: b.left - r.left + b.width / 2,
      y: b.top - r.top + b.height / 2 - shift,
    };

    if (placed.current) {
      const from = posRef.current;
      setTravel(from ? travelMs(to.x - from.x, to.y - from.y) : 780);
      setInstant(false);
      posRef.current = to;
      setPos(to);
      return;
    }
    /* First aim: drop in from below and to one side of the target, then travel
       to it. Expressed as an OFFSET from the measured target rather than a fixed
       start point, so the approach reads the same wherever the control is at
       this width.

       WHICH side is mirrored, because the offset is physical and the layout is
       not. A hand approaches from the side the reader's own hand comes from, and
       at the trailing edge of an RTL shell — tab 5's Save & Publish, tab 4's
       Apply — a fixed +54 also starts the dot outside the frame, so it enters by
       appearing rather than by arriving. `dir` is read off the composed style
       rather than the shell's prop: this component takes no locale, and the
       nearest ancestor's resolved direction is the thing that is actually
       true. */
    placed.current = true;
    setInstant(true);
    const rtl = getComputedStyle(layer).direction === 'rtl';
    const entry = { x: to.x + (rtl ? -54 : 54), y: to.y + 40 };
    posRef.current = entry;
    setPos(entry);
    const raf = requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        setTravel(travelMs(to.x - entry.x, to.y - entry.y));
        setInstant(false);
        posRef.current = to;
        setPos(to);
      })
    );
    return () => cancelAnimationFrame(raf);
  }, [aim]);

  return (
    <div className="pnrec-touchlayer" ref={layerRef} aria-hidden="true">
      <span
        className="pnrec__touch"
        data-visible={visible && pos !== null}
        data-pressing={pressing}
        data-instant={instant}
        style={
          pos
            ? ({
                transform: `translate(${pos.x}px, ${pos.y}px)`,
                '--pnd-travel': `${travel}ms`,
              } as CSSProperties)
            : undefined
        }
      />
    </div>
  );
}
